import { TReportQuestionEmotion, TReportQuestionOption } from "../../types/types";
import { IAppointment } from "./utils.types";
import type { DeviceConfig } from "./device.types";

export interface IFile {
  lastModified?: number;
  lastModifiedDate?: Date;
  name: string;
  size: number;
  type: string;
  webkitRelativePath: string;
}
export type TBaseUser = {
  key: string;
  id: number;
  firstName: string;
  fullName: string;
  lastName: string | null;
  roleId: number;
  phone: string;
  email: string | null;
  birth: string | null;
  avatar: string | null;
  companyId: number;
  account: number | null;
  planId: number;
  fakeCompanyId: number | null;
  recommends?: null | string;
};

export type TServerUser = TBaseUser & {
  guid: string;
  account: string | null;
  password: string | null;
  passport: string | null;
  tempCode: string | null;
  address: string | null;
  planId: number;
  genderId: number | null;
  statusId: number;
  dateRegister: string | null;
  usersFlowId: string | null;
};

export type TAddUserResponse = {
  success?: boolean;
  updatedUserGuid?: string;
  updatedUserId?: number;
  desc?: string;
  isExistInAnotherCompany?: string | null;
};

export type TGetUsersByTherapistIdResponse =
  | {
    TherapistUsers: TServerUser[]; // The first possible shape
    isExistInAnotherCompany?: string | null;
  }
  | {
    success: string; // The second possible shape
    desc: string;
    isExistInAnotherCompany?: string | null;
  };

export type TTakeUserRequest = {
  TherapistsId: number | string;
  Id: string;
  FirstName: string;
  Phone?: string;
  LastName?: string;
  Email?: string;
  Birth?: Date;
  GenderId: number;
  Passport?: string;
  FakeCompanyId?: number | null;
  [key: string]: any;
};
export type TAddUserRequest = {
  id?: number;
  companyId: number;
  fakeCompanyId?: number | null;
  roleId: number;
  changeRole?: boolean;
  firstName: string;
  phone?: string;
  lastName?: string;
  email?: string;
  birth?: string | undefined;
  genderId: number | null;
  passport?: string;
  password?: string;
  statusId: number;
  roles?: { id: number };
  guid: string | null;
  protocolId?: number | null;
  treatmentGroupId?: number | null;
  isConnection?: number | null;
  connectionUserId?: string | number | null;
  contacts?: TContactPerson[];
  cityId?: number | null;
  address?: string;
  paymentCollectionMethodId?: number | null;
  avatar?: string | null;
}

export type TMultyCompanyDetails = {
  CompanyId: number;
  CompanyName: string | null;
  UserId: number;
  ImageUrl: string | null;
};

// Define ContactPerson type (or import it if shared)
export type TContactPerson = {
  firstName: string;
  lastName?: string;
  phone?: string;
  email?: string;
  relation?: string;
  fakeCompanyId?: number | null;
  passport?: string;
  genderId?: number | null;
  roleId?: number;
  statusId?: number;
  id?: number;
  password?: string;
  guid?: string;
  sessionCost?: number | null;
  deviceConfig?: DeviceConfig;
  [key: string]: any;

}
export type TAddTherapistRequest = TAddUserRequest & {
  password?: string;
  specializationsId?: number | null;
  vetek: number | null;
  migzarId: number | null;
  [key: string]: any;
};

export type TUpdateUserRequest = {
  guid: string;
  questionnaireCompaniesHeadersId?: number | null; // Direct mapping to database field
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  birth?: string; // Only send if valid date
  genderId?: number;
  passport?: string;
  statusId?: number;
  roleId?: number;
  fakeCompanyId?: number | null;
  migzarId?: number | null;
  vetek?: number | null;
  specializationsId?: number | null;
  cityId?: number | null;
  address?: string;
  [key: string]: any;
};
export interface IGenderOption {
  id: number;
  name: string;
}

export type TQuestionnaire = {
  Id: number;
  Desc: string;
  Symbol: string;
  StartQuestion: string;
  EndQuestion: string;
  StatusId: number;
  Monthly: null;
  Dayly: null;
  DefaultId: null;
  QuestionnaireTypesId: number;
  TherapistName: null | string;
  TotalAmount?: number | null;
  TotalAmountPay?: number | null;
  QuestionnaireCompaniesScoreUsersStatuses?: number | null;
};

export type TQuestionnaireEntry = {
  Cycle: number;
  QuestionnaireId: number;
  Desc: string;
  QuestionnaireCompaniesId: number;
  TherapistsId: number | null;
  Timestamp: string;
  TotalAmount?: number | null;
  TotalAmountPay?: number | null;
  QuestionnaireCompaniesScoreUsersStatuses?: number | null;
};

/**
 * One selectable therapist questionnaire, as returned by
 * Questions/GetTherapistQuestionnairesForAutoFill (scoped to the caller's company).
 */
export interface TTherapistQuestionnaireForAutoFill {
  questionnaireId: number;
  questionnaireCompaniesId: number;
  /** Every component this questionnaire is linked to. Missing on older API versions. */
  questionnaireCompaniesIds?: number[];
  name: string;
  typeName?: string | null;
  symbol?: string | null;
}

export type TQuestionnaireQuestion = {
  Id: number;
  QuestionnaireId: number;
  DataTypesId: number;
  Desc: string;
  NextQuestionId: number | null;
  StatusId: number;
  Seq: number;
  DescDateTypes?: string;
  Answer?: string | null;
  Score: number;
  OptionsCountMax: number | null;
  OptionsCountMin: number | null;
  IsNoScore: boolean | null;
  CompanyGroupsRRMHeadersId: number | null;
  FactorQuestion: number;
};

export type TAllUserQuestionnairesData = {
  Questionnaires?: TQuestionnaireEntry[];
  QuestionnairesTherapists: TQuestionnaireEntry[];
  QuestionnairesUsers: TQuestionnaireEntry[];
};

export type TAddMessageRequest = {
  QuestionnaireIds: string;
  MessageId: string;
  Title: string;
  Desc: string;
  Score: string;
  UsersId: string;
  Cycle: number | null;
};

export type TRRMListEntry = {
  anliza: string | null;
  color: string;
  companyId: number;
  conversation: string | null;
  desc: string | null;
  highlights: string | null;
  id: number;
  recommendations: string | null;
  rrmseq: number;
  statusId: number;
  title: string;
};

export type TCompanyGroupsRRMEntry = {
  Id: number;
  CompanyId?: number;
  Seq: number;
  QuestionnaireId?: number;
  CompanyGroupsRRMHeadersId?: number;
  Title: string;
  Desc: string | null;
  Color: string;
  Anliza: string | null;
  Recommendations: string | null;
  Highlights: string | null;
  Conversation: string | null;
  Start: number;
  End: number;
  StatusId: number;
};

export type TRRMDisplayEntry = {
  id: number;
  rrmseq: number;
  color: string;
  desc: string | null;
  title: string;
  anliza: string | null;
  recommendations: string | null;
  highlights: string | null;
  conversation: string | null;
  statusId: number;
  rangeStart?: number | null;
  rangeEnd?: number | null;
};

export type THeaderScore = {
  CompanyGroupsRRMHeadersId: number;
  Desc: string;
  Score: number;
  CompanyGroupsRRM?: TCompanyGroupsRRMEntry[] | null;
};
export type TQuestionnaireCompanies = {
  Id: number;
  CompanyId: number;
  QuestionnaireCompaniesHeadersId: number;
  QuestionnaireCompaniesTypeId: number;
  StatusId: Number;
  TherapistQuestionnaireId: number;
  UserQuestionnaireId: number;
  Dayly: number | null;
  Monthly: number | null;
  Weekly: number | null;
};
export type TQuestionnaireContainer = {
  FakeCompanyId?: number | null;
  Questionnaire: TQuestionnaire;
  Questions: TQuestionnaireQuestion[];
  QuestionnaireCompanies: TQuestionnaireCompanies;
  TotalAmount?: number | null;
  CompanyServiceId: number | null;
  CompanyServiceName: string | null;
  TotalAmountPay?: number | null;
};
// Type for questionnaire score entries
export type TQuestionnaireScore = {
  Cycle: number;
  Name: string;
  QuestionnaireCompaniesId: number;
  QuestionnaireId: number;
  Score: number;
  RRM: number;
  TherapistsId: number | null;
  TherapistsName: string | null;
  HeaderScores?: THeaderScore[] | null;
};

// Type for total MRR score
export type TTotalScoreRRM = {
  TotalScore: number;
  RRM: number;
};
export type TMessage = {
  Id: number;
  QuestionnaireIds: string;
  Score: number;
  MessagesId: number;
};
export type TGetMultipleQuestionnairesByUserIdAndCycleResponse = {
  TherapistQuestionnaire: TQuestionnaireContainer;
  UserQuestionnaire: TQuestionnaireContainer;
  User: TBaseUser;
  RRMList: TRRMListEntry[];
  RRMAndScore?: TQuestionnaireScore[];
};

export type TGetMultipleQuestionnairesByUserIdAndCycleRequest = {
  UsersGuid: string;
  Cycle: number;
  QuestionnaireCompaniesId: number;
};

export type TQuestionsOptionsData = {
  Questions: TQuestionnaireQuestion;
  QuestionsOptions: TReportQuestionOption[];
};

export type TQuestionsEmotionsData = {
  Questions: TQuestionnaireQuestion;
  QuestionsEmotions: TReportQuestionEmotion[];
};

export type TGetAllQuestionsAndOptionsResponse = {
  questionsOptions: TQuestionsOptionsData[]
  questionsEmotions: TQuestionsEmotionsData[]
}


export type TInsertAnswerRequest = {
  Id: string | number;
  QuestionnaireId: string;
  QuestionsId: string;
  Answer: string;
};
export type TInsertAllAnswersRequest = TInsertAnswerRequest & {
  AnswerDate: Date | null;
  QuestionsOptionsIds: string;
  Cycle?: number | string | null;
  questionnaireCompaniesScoreUsersId?: number | null;
};
export type TInsertAllAnswersQuestionsResponse = {
  success: boolean;
  userguid: string;
  questionnaireCompaniesScoreUsersId: number;
  processing: boolean;
};

export type TQuestionsOptionsPreferencesRequest = {
  body: TQuestionsOptionsPreferencesBodyRequest;
  questionnairId: number;
  companyId: number
}

export type TQuestionsOptionsPreferencesBodyRequest = {
  selections: Record<string, number[]>;
  users?: number[];
  startDate?: string;
  endDate?: string;
  includeAnonymous?: boolean;
}


export type TGetAlllDahboardDataResponse = {
  totalUsers: number;
  totalTimes: number;
  questionsAnalyzes: TQuestionData[];
  users: number[] | null;
  companies: { id: number; name: string; }[];
  questionnaireId?: number;
  startDate: string | null;
  endDate: string | null;
  isResearch?: boolean;
  includeAnonymous?: boolean;
}

export type TQuestionData = {
  desc: string;
  questionsId: number;
  options: TOptionData[];
  groups: TGroup[] | null;
  diagramsTypesId: number;
  questionsPreferencesId: number;
  isEmotion: boolean;
  totalResponds: number | null;
}

export type TOptionData = {
  answersCount: number;
  desc: string;
  optionId: number;
  answerPercentage: number;
  group?: {
    groupId: number;
    groupName: string;
  }
  emotion?: {
    emotionId: number;
    emotionName: string;
    isTotal: boolean;
  }
}

export type TGroup = {
  groupId: number;
  groupName: string;
}
export interface ITherapistJoiningData {
  date: string;
  count: number;
}
export interface GraphDataPoint {
  category: string; // x-axis label (date, category, etc.)
  [seriesName: string]: number | string;
}

export interface ITherapistsRoleJoinAnalysis {
  roleId: number;
  roleName: string;
  totalCurrentUsers: number;
  totalJoining: number;
  totalLefting: number;
  joinings: ITherapistJoiningData[];
}

export type TTotalAnalyze = {
  count: number;
  percent: number;
};

export type TAppointmentsPatientsTrend = {
  count: number;
  changePercent: number | null;
};

export type TAppointmentsPatientsPeriodTrend = {
  appointments: TAppointmentsPatientsTrend;
  patients: TAppointmentsPatientsTrend;
};

export type TAppointmentsAndPatientsTrends = {
  lastMonth: TAppointmentsPatientsPeriodTrend;
  quarterly: TAppointmentsPatientsPeriodTrend;
  yearly: TAppointmentsPatientsPeriodTrend;
};

export type TTherapistsJoinAnalysisResult = {
  totalJoining: TTotalAnalyze;
  totalLefting: TTotalAnalyze;
  analyzes: ITherapistsRoleJoinAnalysis[];
  companyName: string;
  companyLogoUrl?: string;
  stateAnalysis?: TStateAnalysisEntry[];
  appointmentsAndPatientsTrends?: TAppointmentsAndPatientsTrends;
};

export type TStateAnalysisEntry = {
  date: string;
  totalTherapists: number;
  roles: TStateAnalysisRole[];
};

export type TStateAnalysisRole = {
  roleId: number;
  roleName: string;
  count: number;
};

export type TTherapistsJoinAnalysisRequest = {
  startDate?: string;
  endDate?: string;
  companyId: number;
};

export type TTherapistsRolesAvailability = {
  roleId: number;
  roleName: string;
  totalHours: number;
}

// Export Reports Types
export type TExportQuestionnaireRequest = {
  userId: number;
  questionnaireCompaniesId: number;
  questionnaireId: number;
  cycle: number;
  language?: string;
  userRole?: number;
};

export type TExportTherapistActionRequest = {
  appointmentId: number;
  userId: number;
  therapistId: number;
  cycle: number;
  questionnaireId: number;
  questionnaireCompaniesId: number;
  language?: string;
  userRole?: number;
};

export type TExportReportResponse = {
  success: boolean;
  fileUrl: string;
  viewerUrl?: string;
  fileType: "pdf" | "excel";
};

export type TUpdateTherapistsUsersConnectionsRequest = {
  therapistsGuids: string[];
  usersGuids: string[];
};

export type TUpdateTherapistsUsersConnectionsResponse = {
  success: boolean;
  action: string;
  results: {
    therapistsProcessed: number;
    usersProcessed: number;
    connectionsAffected: number;
    connectionsSkipped: number;
    totalPotentialConnections?: number;
  };
};
export type TTherapist = {
  id: number;
  guid: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  companyId: number;
  fakeCompanyId: number | null;
  statusId: number;
  hasDevices?: boolean;
  deviceCount?: number;
};

export type TTherapistQuestionReport = {
  Id: number;
  AppointmentDate: string | null;
  StartTime: string | null;
  EndTime: string | null;
  QuestionnaireCompaniesScoreUsersId: number;
  TherapistName: string;
  TherapistId: number;
  TherapistsGuid: string;
  UserId: number;
  UsersGuid: string;
  UserFirstName: string;
  UserLastName: string;
  UserPhone?: string;
  UserBirth?: string;
  Timestamp: string;
  StatusId: number;
  StatusName: string;
  Cycle: number;
  QuestionnaireName: string;
  QuestionnaireId: number;
  QuestionnaireCompaniesId: number;
  QuestionnaireCompaniesTypeId: number;
  Score?: number;
  RRM?: number;
  TotalAmount?: number | null;
  TotalAmountPay?: number | null;
  AppointmentsId?: number;
  HasTherapistAnswered: boolean;
};

export type TTherapistsQuestionsReportsResponse = TTherapistQuestionReport[];

export type TTherapistsQuestionsReportsFilters = {
  therapistName?: string;
  userName?: string;
  questionnaireName?: string;
  statusId?: string;
  filled?: string;
  dateFrom?: string;
  dateTo?: string;
};

export type TGetQuestionnaireAnswersForEditResponse = {
  QuestionnaireCompaniesScoreUsersId: number;
  Cycle?: number;
  QuestionnaireId?: number;
  QuestionnaireCompaniesId?: number;
  DefaultName?: string | null;
  TotalAmount?: number | null;
  TotalAmountPay?: number | null;
  CompanyServiceId?: number | null;
  QuestionnaireCompaniesScoreUsersStatusesId?: number | null;
  Questions: Array<{
    QuestionId: number;
    DataTypesId: number;
    Answer?: string | null;
    SelectedOptions?: Array<{
      OptionId: number;
      GroupId: number | null;
    }>;
    CheckboxValue?: boolean;
  }>;
};

export interface TTherapistAvailability {
  id?: number;
  companyId: number;
  therapistsId: number;
  dayId: number;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  statusId: number;
  specificDate?: string | null;
}
export type TUpdateUserBalanceRequest = {
  userGuid: string;
  newBalance: number;
  updateReason: string;
}

export type TUpdateUserBalanceResponse = {
  guid: string;
  newBalance: number;
}
export type TPendingReport = {
  appointment: IAppointment;
  userName: string | null;
  therapistName: string | null;

}
export type TPendingInquiry = {
  num: number;
  usersId: number;
  userName: string;

  firstName: string;
  lastName: string;
  phone: string;
  birth: string; // ISO date string
  guid: string;

  therapistId: number;
  therapistName: string;
  therapistQuestionnaireId: number;

  questionnaireCompaniesId: number;
  questionnaireCompaniesTypeId: number;
  questionnaireCompaniesType: string;

  cycle: number;
  timestamp: string;
};
export type TClientUploadedFile = {
  id: number;
  fileName: string;
  subject?: string;
  urgency?: string;
  details?: string;
  dateUploaded?: string;
};

/**
 * Paycall / webpbx softphone credentials for one agent (backend: AgentSipCredentials).
 * At most one active row per agent. statusId: 1 = active, 0 = soft-deleted.
 */
export type TAgentSipCredentials = {
  id: number;
  usersId?: number;
  sipUsername: string;
  sipPassword?: string | null;
  sipDomain?: string | null;
  sipPort?: number | null;
  wsServerUrl?: string | null;
  statusId?: number | null;
};

export interface IPendingReportResponce {
  notReported: TPendingInquiry[];
  notReportedTotal: number;
  pendingAppointments: TPendingReport[];
  pendingAppointmentsTotal: number;
  therapists: {id:number; fullName: string;}[]
}

// --- Clinical Findings (MeetingRecords/clinical-findings) ---

export type TClinicalRaisedBy = 'clinician' | 'client';
export type TClinicalClientResponse = 'answered' | 'not_answered';

export type TRiskConcern = {
  category: string | null;      // enum — localize
  basis: string | null;         // only set when category = impaired_judgment_or_reality_testing
  categoryLabel: string | null; // model's own label — stored, not rendered
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
};

export type TSuicidalityFinding = {
  recordNumber: number | null;
  value: number | null; // 0-3; null when the API sent no usable number
  temporalContext: string | null;
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: TClinicalRaisedBy | null;
  clientResponse: TClinicalClientResponse | null;
  absenceReason: string | null;
  statusLine: string | null;         // render exactly as returned, never translate
  statusLineLanguage: string | null;
  alert: boolean;
  textLanguage: string | null;
  extractionNotes: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TRiskFinding = {
  recordNumber: number | null;
  value: number | null; // 0-2; null when the API sent no usable number
  concerns: TRiskConcern[];
  absenceReason: string | null;
  absencePhrase: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
  statusLine: string | null;
  statusLineLanguage: string | null;
  alert: boolean;
  textLanguage: string | null;
  extractionNotes: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TMedicationMention = {
  verbatimPhrase: string | null;
  matchedIngredient: string | null; // often "not_sure" — don't build logic on it
  resolution: string | null;        // enum — localize
  temporalStatus: string | null;    // enum — localize; only when resolution = yes
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
  statusLine: string | null;        // render exactly as returned
};

export type TMedicationFlag = {
  mentionStatus: string | null; // not_mentioned | not_applicable | has_mentions
  mentions: TMedicationMention[];
  extractionNotes: string | null;
};

export type TMedicationFinding = {
  recordNumber: number | null;
  prescribed: TMedicationFlag;
  selfDirected: TMedicationFlag;
  statusLineLanguage: string | null;
  textLanguage: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TRecommendationItem = {
  tier: number | null; // 1 = agreed, 2 = to be decided
  itemType: string | null;
  category: string | null;      // enum — localize; "other" shows categoryLabel
  categoryLabel: string | null;
  recommendationLine: string | null; // render exactly as returned
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
  suggestedNeed: string | null;
  suggestedNeedLabel: string | null;
};

export type TRecommendationsFinding = {
  recordNumber: number | null;
  status: string | null; // has_items | no_recommendations | cant_tell
  items: TRecommendationItem[];
  statusLine: string | null; // only set when there are no items
  statusLineLanguage: string | null;
  extractionNotes: string | null;
  textLanguage: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TClinicalFindingsResponse = {
  latestSuicidality: TSuicidalityFinding | null;
  latestRisk: TRiskFinding | null;
  latestMedication: TMedicationFinding | null;
  latestRecommendations: TRecommendationsFinding | null;
  suicidalityHistory: TSuicidalityFinding[]; // newest first, includes latest
  riskHistory: TRiskFinding[];               // newest first, includes latest
  medicationHistory: TMedicationFinding[];   // newest first, includes latest
  recommendationsHistory: TRecommendationsFinding[]; // newest first, includes latest
};

/**
 * Field casing for this endpoint hasn't been confirmed against a live response
 * (PascalCase unless the API has a global camelCase policy), so every field is
 * read under either casing — same defensive approach as pickOptionalNullableInt
 * / mapMeetingRecordFromApi above.
 */
function pickField<T = unknown>(item: Record<string, unknown>, key: string): T | undefined {
  if (key in item) return item[key] as T;
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  if (pascalKey in item) return item[pascalKey] as T;
  return undefined;
}

function pickStringField(item: Record<string, unknown>, key: string): string | null {
  const v = pickField<string>(item, key);
  return v ?? null;
}

// A missing or bad number becomes null. Plain Number() is not enough: it turns undefined into NaN
// ("NaN/3") and turns " ", true or [] into 0 or 1, which would show a made-up score.
function pickNumberField(item: Record<string, unknown>, key: string): number | null {
  const v = pickField(item, key);
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string" || v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickBooleanField(item: Record<string, unknown>, key: string): boolean {
  return Boolean(pickField<boolean>(item, key));
}

function mapRiskConcernFromApi(item: Record<string, unknown>): TRiskConcern {
  return {
    category: pickStringField(item, "category"),
    basis: pickStringField(item, "basis"),
    categoryLabel: pickStringField(item, "categoryLabel"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
  };
}

export function mapSuicidalityFindingFromApi(item: Record<string, unknown>): TSuicidalityFinding {
  return {
    recordNumber: pickNumberField(item, "recordNumber"),
    value: pickNumberField(item, "value"),
    temporalContext: pickStringField(item, "temporalContext"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy") as TClinicalRaisedBy | null,
    clientResponse: pickStringField(item, "clientResponse") as TClinicalClientResponse | null,
    absenceReason: pickStringField(item, "absenceReason"),
    statusLine: pickStringField(item, "statusLine"),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    alert: pickBooleanField(item, "alert"),
    textLanguage: pickStringField(item, "textLanguage"),
    extractionNotes: pickStringField(item, "extractionNotes"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

function mapRiskFindingFromApi(item: Record<string, unknown>): TRiskFinding {
  const concerns = pickField<Record<string, unknown>[]>(item, "concerns") ?? [];
  return {
    recordNumber: pickNumberField(item, "recordNumber"),
    value: pickNumberField(item, "value"),
    concerns: Array.isArray(concerns) ? concerns.map(mapRiskConcernFromApi) : [],
    absenceReason: pickStringField(item, "absenceReason"),
    absencePhrase: pickStringField(item, "absencePhrase"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
    statusLine: pickStringField(item, "statusLine"),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    alert: pickBooleanField(item, "alert"),
    textLanguage: pickStringField(item, "textLanguage"),
    extractionNotes: pickStringField(item, "extractionNotes"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

function mapMedicationMentionFromApi(item: Record<string, unknown>): TMedicationMention {
  return {
    verbatimPhrase: pickStringField(item, "verbatimPhrase"),
    matchedIngredient: pickStringField(item, "matchedIngredient"),
    resolution: pickStringField(item, "resolution"),
    temporalStatus: pickStringField(item, "temporalStatus"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
    statusLine: pickStringField(item, "statusLine"),
  };
}

function mapMedicationFlagFromApi(item: Record<string, unknown> | undefined): TMedicationFlag {
  const flag = item ?? {};
  const mentions = pickField<Record<string, unknown>[]>(flag, "mentions") ?? [];
  return {
    mentionStatus: pickStringField(flag, "mentionStatus"),
    mentions: Array.isArray(mentions) ? mentions.map(mapMedicationMentionFromApi) : [],
    extractionNotes: pickStringField(flag, "extractionNotes"),
  };
}

function mapMedicationFindingFromApi(item: Record<string, unknown>): TMedicationFinding {
  return {
    recordNumber: pickNumberField(item, "recordNumber"),
    prescribed: mapMedicationFlagFromApi(pickField(item, "prescribed")),
    selfDirected: mapMedicationFlagFromApi(pickField(item, "selfDirected")),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    textLanguage: pickStringField(item, "textLanguage"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

function mapRecommendationItemFromApi(item: Record<string, unknown>): TRecommendationItem {
  return {
    tier: pickNumberField(item, "tier"),
    itemType: pickStringField(item, "itemType"),
    category: pickStringField(item, "category"),
    categoryLabel: pickStringField(item, "categoryLabel"),
    recommendationLine: pickStringField(item, "recommendationLine"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
    suggestedNeed: pickStringField(item, "suggestedNeed"),
    suggestedNeedLabel: pickStringField(item, "suggestedNeedLabel"),
  };
}

function mapRecommendationsFindingFromApi(item: Record<string, unknown>): TRecommendationsFinding {
  const items = pickField<Record<string, unknown>[]>(item, "items") ?? [];
  return {
    recordNumber: pickNumberField(item, "recordNumber"),
    status: pickStringField(item, "status"),
    items: Array.isArray(items) ? items.map(mapRecommendationItemFromApi) : [],
    statusLine: pickStringField(item, "statusLine"),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    extractionNotes: pickStringField(item, "extractionNotes"),
    textLanguage: pickStringField(item, "textLanguage"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

/** Maps a MeetingRecords/clinical-findings response to TClinicalFindingsResponse. */
export function mapClinicalFindingsFromApi(raw: Record<string, unknown>): TClinicalFindingsResponse {
  const latestSuicidality = pickField<Record<string, unknown>>(raw, "latestSuicidality");
  const latestRisk = pickField<Record<string, unknown>>(raw, "latestRisk");
  const latestMedication = pickField<Record<string, unknown>>(raw, "latestMedication");
  const latestRecommendations = pickField<Record<string, unknown>>(raw, "latestRecommendations");
  const suicidalityHistory = pickField<Record<string, unknown>[]>(raw, "suicidalityHistory") ?? [];
  const riskHistory = pickField<Record<string, unknown>[]>(raw, "riskHistory") ?? [];
  const medicationHistory = pickField<Record<string, unknown>[]>(raw, "medicationHistory") ?? [];
  const recommendationsHistory = pickField<Record<string, unknown>[]>(raw, "recommendationsHistory") ?? [];

  return {
    latestSuicidality: latestSuicidality ? mapSuicidalityFindingFromApi(latestSuicidality) : null,
    latestRisk: latestRisk ? mapRiskFindingFromApi(latestRisk) : null,
    latestMedication: latestMedication ? mapMedicationFindingFromApi(latestMedication) : null,
    latestRecommendations: latestRecommendations ? mapRecommendationsFindingFromApi(latestRecommendations) : null,
    suicidalityHistory: Array.isArray(suicidalityHistory) ? suicidalityHistory.map(mapSuicidalityFindingFromApi) : [],
    riskHistory: Array.isArray(riskHistory) ? riskHistory.map(mapRiskFindingFromApi) : [],
    medicationHistory: Array.isArray(medicationHistory) ? medicationHistory.map(mapMedicationFindingFromApi) : [],
    recommendationsHistory: Array.isArray(recommendationsHistory) ? recommendationsHistory.map(mapRecommendationsFindingFromApi) : [],
  };
}
